import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(
    localStorage.getItem("activeCompanyId") || null
  );

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      if (data.length && (!activeCompanyId || !data.find((c) => c.id === activeCompanyId))) {
        setActiveCompanyId(data[0].id);
        localStorage.setItem("activeCompanyId", data[0].id);
      }
      return data;
    } catch (_e) {
      setCompanies([]);
      return [];
    }
  }, [activeCompanyId]);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth, let AuthCallback handle it first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadCompanies();
      } catch (_e) {
        setUser(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    await api.post("/auth/login", { email, password });
    // Fetch the full profile (includes is_superadmin, plan_status, etc.) so the
    // sidebar can render superadmin-only sections immediately without a reload.
    const { data: me } = await api.get("/auth/me");
    setUser(me);
    await loadCompanies();
    return me;
  };

  const register = async (email, password, name) => {
    await api.post("/auth/register", { email, password, name });
    const { data: me } = await api.get("/auth/me");
    setUser(me);
    await loadCompanies();
    return me;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {}
    setUser(false);
    setCompanies([]);
    setActiveCompanyId(null);
    localStorage.removeItem("activeCompanyId");
  };

  const switchCompany = (id) => {
    setActiveCompanyId(id);
    localStorage.setItem("activeCompanyId", id);
  };

  const activeCompany = companies.find((c) => c.id === activeCompanyId) || null;

  return (
    <AuthCtx.Provider
      value={{
        user,
        companies,
        activeCompany,
        activeCompanyId,
        switchCompany,
        loadCompanies,
        login,
        register,
        logout,
        setUser,
        formatApiError,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
